import { BodyText } from '@/components/BodyText';
import { Ellipsis } from '@/components/Ellipsis';
import { StatusBadge } from '@/components/StatusBadge';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useTenants } from '@/services/tenants/useTenants';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { Box, CircularProgress, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { GridColDef } from '@mui/x-data-grid-pro';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { getBatchLabel } from './batchLabel';
import { UnifiedRow } from './activityRows';
import { PartFinderBatchThumbnail } from './PartFinderBatchThumbnail';
import { PartFinderElapsedCell } from './PartFinderElapsedTime';
import { PartFinderStatusCell } from './PartFinderStatusCell';
import { PartFinderThumbnailCell } from './PartFinderThumbnailCell';
import { TopRecommendationCell } from './TopRecommendationCell';

type GetPath = ReturnType<typeof useTenants>['getPath'];
type SelfAwareEmail = ReturnType<typeof useGetCurrentUser>['selfAwareEmail'];

const NameCell = styled(Box)({ display: 'inline-flex', alignItems: 'center', gap: '4px', minWidth: 0 });

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&:hover': { textDecoration: 'underline' },
}));

interface ActivityColumnsArguments {
  expandedBatchIds: Set<string>;
  loadingBatchIds: Set<string>;
  toggleBatch: (rowId: string, batchUuid: string) => void;
  getPath: GetPath;
  selfAwareEmail: SelfAwareEmail;
  thumbnailsEnabled: boolean;
}

const thumbnailColumn: GridColDef<UnifiedRow> = {
  field: 'thumbnail',
  headerName: '',
  width: 63,
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  renderCell: parameters =>
    parameters.row.kind === 'report' ? (
      <PartFinderThumbnailCell report={parameters.row.report} />
    ) : (
      <PartFinderBatchThumbnail batch={parameters.row.batch} />
    ),
};

export const buildActivityColumns = ({
  expandedBatchIds,
  loadingBatchIds,
  toggleBatch,
  getPath,
  selfAwareEmail,
  thumbnailsEnabled,
}: ActivityColumnsArguments): GridColDef<UnifiedRow>[] => [
  ...(thumbnailsEnabled ? [thumbnailColumn] : []),
  {
    field: 'partNumber',
    headerName: 'Part Number',
    width: 240,
    flex: 1,
    sortable: false,
    renderCell: parameters => {
      if (parameters.row.kind === 'batch') {
        const rowId = parameters.row.id;
        const batch = parameters.row.batch;
        const isExpanded = expandedBatchIds.has(rowId);
        const isLoading = loadingBatchIds.has(rowId);
        let toggleIcon = <ChevronRight fontSize='small' />;
        if (isLoading) {
          toggleIcon = <CircularProgress size={16} />;
        } else if (isExpanded) {
          toggleIcon = <ExpandMore fontSize='small' />;
        }
        return (
          <NameCell>
            <IconButton
              size='small'
              aria-label={isExpanded ? 'Collapse batch' : 'Expand batch'}
              onClick={event => {
                event.stopPropagation();
                toggleBatch(rowId, batch.id);
              }}
            >
              {toggleIcon}
            </IconButton>
            <StyledLink to={getPath(`part-finder/batches/${batch.id}`)}>
              <Ellipsis text={getBatchLabel(batch)} />
            </StyledLink>
          </NameCell>
        );
      }
      const report = parameters.row.report;
      return (
        <StyledLink to={getPath(`part-finder-reports/${report.id}`)}>
          <Ellipsis text={report.partNumber} />
        </StyledLink>
      );
    },
  },
  {
    field: 'description',
    headerName: 'Description',
    flex: 2,
    minWidth: 200,
    sortable: false,
    renderCell: parameters =>
      parameters.row.kind === 'report' && parameters.row.report.description ? (
        <BodyText>{parameters.row.report.description}</BodyText>
      ) : (
        <BodyText sx={{ color: 'text.secondary' }}>—</BodyText>
      ),
  },
  {
    field: 'topRecommendation',
    headerName: 'Top Recommendation',
    flex: 2,
    minWidth: 360,
    sortable: false,
    renderCell: parameters =>
      parameters.row.kind === 'report' ? (
        <TopRecommendationCell report={parameters.row.report} />
      ) : (
        <BodyText sx={{ color: 'text.secondary' }}>—</BodyText>
      ),
  },
  {
    field: 'creator',
    headerName: 'Created By',
    width: 130,
    flex: 1,
    sortable: false,
    renderCell: parameters => {
      const email =
        parameters.row.kind === 'report' ? parameters.row.report.creator?.email : parameters.row.batch.creator?.email;
      return <BodyText>{selfAwareEmail(email || '')}</BodyText>;
    },
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    width: 120,
    flex: 1,
    sortable: false,
    renderCell: parameters =>
      parameters.row.createdAt ? <BodyText>{format(new Date(parameters.row.createdAt), 'MMM d, yyyy')}</BodyText> : '',
  },
  {
    field: 'elapsed',
    headerName: 'Elapsed',
    width: 100,
    sortable: false,
    renderCell: parameters =>
      parameters.row.kind === 'report' ? (
        <PartFinderElapsedCell row={parameters.row.report} />
      ) : (
        <BodyText sx={{ color: 'text.secondary' }}>—</BodyText>
      ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 150,
    flex: 1,
    sortable: false,
    renderCell: parameters =>
      parameters.row.kind === 'report' ? (
        <PartFinderStatusCell row={parameters.row.report} />
      ) : (
        <StatusBadge status={parameters.row.batch.status} progress={parameters.row.batch.progress} />
      ),
  },
];
